import { IWebsiteBuilderGridCell } from "pages/secure/facility/websiteBuilder/editor/nodes/gridCell/websiteBuilderGridCell";
import { IWebsiteBuilderFooter } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderFooter";
import { IWebsiteBuilderHeader } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderHeader";
import { cloneDeep } from "lodash";
import { IWebsiteBuilderEditorHistoryActionBase, IWebsiteBuilderHistorySnapshot, WebsiteBuilderEditorActionType } from "../../websiteBuilderEditorAction";
import { IWebsiteBuilderGeneralTraversalNode } from "../../actionHelpers/generalTraversal";
import { isWebsiteBuilderGrid, IWebsiteBuilderGrid } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderGrid";
import { IWebsiteBuilderRoot } from "pages/secure/facility/websiteBuilder/editor/nodes/root/websiteBuilderRoot";

export interface IUpdateGridDimensionsActionPayload {
  id: string;
  prevRows: number;
  nextRows: number;
  prevColumns: number;
  nextColumns: number;
  prevCells: IWebsiteBuilderGridCell[];
  nextCells: IWebsiteBuilderGridCell[];
}

export interface IUpdateGridDimensionsAction extends IWebsiteBuilderEditorHistoryActionBase {
  type: typeof WebsiteBuilderEditorActionType.UPDATE_GRID_DIMENSIONS;
  payload: IUpdateGridDimensionsActionPayload;
}

export function getUpdateGridDimensionsInverseAction(
  action: IUpdateGridDimensionsAction,
): IUpdateGridDimensionsAction {
  const {
    id,
    prevRows,
    nextRows,
    prevColumns,
    nextColumns,
    prevCells,
    nextCells,
  } = action.payload;

  return {
    type: WebsiteBuilderEditorActionType.UPDATE_GRID_DIMENSIONS,
    pageId: action.pageId,
    device: action.device,
    scrollNodeId: action.scrollNodeId,
    payload: {
      id,
      prevRows: nextRows,
      nextRows: prevRows,
      prevColumns: nextColumns,
      nextColumns: prevColumns,
      prevCells: cloneDeep(nextCells),
      nextCells: cloneDeep(prevCells),
    },
  };
}

type TUpdateGridDimensionsTraversalNode = IWebsiteBuilderGrid | IWebsiteBuilderGeneralTraversalNode;

function updateGridDimensions(
  header: IWebsiteBuilderHeader,
  root: IWebsiteBuilderRoot,
  footer: IWebsiteBuilderFooter,
  id: string,
  nextRows: number,
  nextColumns: number,
  nextCells: IWebsiteBuilderGridCell[],
): IWebsiteBuilderHistorySnapshot {
  function traverse(node: TUpdateGridDimensionsTraversalNode): TUpdateGridDimensionsTraversalNode {
    if (!node.children) {
      return node;
    } else {
      if (isWebsiteBuilderGrid(node) && node.id === id) {
        return {
          ...node,
          container: {
            ...node.container,
            settings: {
              ...node.container.settings,
              rows: nextRows,
              columns: nextColumns,
            },
          },
          children: nextCells,
        };
      } else {
        return {
          ...node,
          children: node.children.map(traverse),
        };
      }
    }
  }

  const updatedHeader = traverse(header) as IWebsiteBuilderHeader;
  const updatedRoot = traverse(root) as IWebsiteBuilderRoot;
  const updatedFooter = traverse(footer) as IWebsiteBuilderFooter;

  return {
    header: updatedHeader,
    root: updatedRoot,
    footer: updatedFooter,
  };
}

export function applyUpdateGridDimensionsChange(
  header: IWebsiteBuilderHeader,
  root: IWebsiteBuilderRoot,
  footer: IWebsiteBuilderFooter,
  action: IUpdateGridDimensionsAction,
): IWebsiteBuilderHistorySnapshot {
  const { id, nextRows, nextColumns, nextCells } = action.payload;

  return updateGridDimensions(header, root, footer, id, nextRows, nextColumns, nextCells);
}